import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog } from "@/components/ui/dialog";
import {
  User,
  BotMessageSquare,
  Boxes,
  Cable,
  ChevronRight,
  Download,
  FolderGit2,
  History,
  Gauge,
  LayoutGrid,
  LogOut,
  Menu,
  MessageCircleQuestion,
  Search,
  ScrollText,
  Settings,
  SquareTerminal,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SidebarView } from "@/components/board-sidebar";
import { useAuth } from "@/lib/auth";
import { useInstallOption } from "@/lib/pwa";
import { hasRole, ROLE_LABELS, type BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";
import { myOpenTotal } from "@/lib/waiting";

type Tray = "more" | null;

/**
 * The phone and tablet navigation: tabs pinned under the content, in place of
 * the sidebar: Status, Awaiting (admins, with its count), Boards, Agents, Apps
 * and More (My tasks, with its count on the tab, Activity, Projects and Settings).
 * It sits in the page's own column rather than floating over it, so the last card and the composer end where the bar begins and nothing needs
 * padding to dodge it. The bar owns the bottom safe-area inset; App sets
 * `--safe-bottom` to 0 so the views above it do not pad for the home indicator
 * a second time.
 *
 * Boards behaves the way a tab does on iOS: it brings back the board last open,
 * and tapped again while on a board it goes up to the Boards list. More opens a
 * tray rather than a view, because what it holds are the registers the sidebar
 * carries, which are dialogs anyway.
 */
export function BottomTabs({
  boards,
  view,
  lastBoardId,
  onSelect,
  onConnectClaude,
  projectCount,
  waitingAgents,
  awaitingAnswer,
  reviewCount,
  onSearch,
}: {
  /** Live boards only, for the badges. */
  boards: BoardDetail[];
  view: SidebarView;
  /** The board the Boards tab reopens, if it still exists. */
  lastBoardId: string | null;
  onSelect: (view: SidebarView) => void;
  /** Present only while a board is open — the config is for that board. */
  onConnectClaude?: () => void;
  projectCount: number;
  waitingAgents: number;
  /** Cards waiting on the user's answer; undefined (below admin) hides the entry. */
  awaitingAnswer?: number;
  /** Cards waiting on the user's review, shown beside My tasks in the tray. */
  reviewCount?: number;
  /** Opens the command palette: the tray's first row. */
  onSearch?: () => void;
}) {
  const { user, logout } = useAuth();
  const [tray, setTray] = useState<Tray>(null);
  const installOption = useInstallOption();

  // Board stats count the owner's row; anyone else's own list is one tap away.
  const myTotal = myOpenTotal(user.id, boards);
  const openMentions = boards.reduce((sum, board) => sum + board.stats.openMentions, 0);


  const go = (next: SidebarView) => {
    setTray(null);
    onSelect(next);
  };
  const then = (action: () => void) => () => {
    setTray(null);
    action();
  };

  return (
    <>
      <nav
        aria-label="Navigation"
        className="flex shrink-0 border-t border-border/70 bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
      >
        <Tab
          icon={<Gauge />}
          label="Status"
          active={tray === null && view.kind === "status"}
          onClick={() => go({ kind: "status" })}
        />
        {awaitingAnswer !== undefined ? (
          // A tab of its own, always there, so answering is one tap from anywhere
          // — the sidebar keeps it at the top for the same reason.
          <Tab
            icon={<MessageCircleQuestion />}
            label="Awaiting"
            count={awaitingAnswer}
            tint="var(--waiting)"
            active={tray === null && view.kind === "waiting"}
            onClick={() => go({ kind: "waiting" })}
          />
        ) : null}
        <Tab
          icon={<LayoutGrid />}
          label="Boards"
          count={openMentions}
          tint="var(--kind-review)"
          active={tray === null && (view.kind === "board" || view.kind === "boards")}
          onClick={() =>
            go(
              view.kind !== "board" && view.kind !== "boards" && lastBoardId
                ? { kind: "board", boardId: lastBoardId }
                : { kind: "boards" },
            )
          }
        />
        <Tab
          icon={<BotMessageSquare />}
          label="Agents"
          count={waitingAgents}
          tint="var(--waiting)"
          active={tray === null && view.kind === "agents"}
          onClick={() => go(view.kind === "agents" ? view : { kind: "agents" })}
        />
        <Tab
          icon={<Boxes />}
          label="Apps"
          active={tray === null && view.kind === "apps"}
          onClick={() => go({ kind: "apps" })}
        />
        <Tab
          icon={<Menu />}
          label="More"
          count={myTotal}
          tint="var(--kind-active)"
          active={
            tray === "more" ||
            (tray === null && (view.kind === "settings" || view.kind === "queue" || view.kind === "activity" || view.kind === "projects" || view.kind === "changelog" || view.kind === "terminal"))
          }
          onClick={() => setTray("more")}
        />
      </nav>

      <TabTray open={tray === "more"} onClose={() => setTray(null)} title="More">
        <div className="space-y-0.5 overflow-y-auto px-2 pb-2">
          {onSearch ? <TrayRow icon={<Search />} label="Search…" onClick={then(onSearch)} /> : null}
          <TrayRow
            icon={<User />}
            label="My tasks"
            count={myTotal}
            note={reviewCount ? `${reviewCount} to review` : undefined}
            active={view.kind === "queue"}
            onClick={() => go({ kind: "queue" })}
          />
          <TrayRow
            icon={<ScrollText />}
            label="Activity"
            active={view.kind === "activity"}
            onClick={() => go({ kind: "activity" })}
          />
          <TrayRow
            icon={<FolderGit2 />}
            label="Projects"
            count={projectCount}
            active={view.kind === "projects"}
            onClick={() => go({ kind: "projects" })}
          />
          {hasRole(user, "owner") ? (
            <TrayRow
              icon={<SquareTerminal />}
              label="Terminal"
              active={view.kind === "terminal"}
              onClick={() => go({ kind: "terminal" })}
            />
          ) : null}
          <TrayRow
            icon={<History />}
            label="Changelog"
            active={view.kind === "changelog"}
            onClick={() => go({ kind: "changelog" })}
          />
          <TrayRow
            icon={<Settings />}
            label="Settings"
            active={view.kind === "settings"}
            onClick={() => go({ kind: "settings" })}
          />
          {onConnectClaude ? (
            <TrayRow icon={<Cable />} label="Connect Claude Code to this board" onClick={then(onConnectClaude)} />
          ) : null}
          {installOption ? (
            <TrayRow icon={<Download />} label={installOption.label} onClick={then(installOption.install)} />
          ) : null}
        </div>
        <div className="flex items-center gap-2 border-t border-border/70 py-3 pl-2 pr-4">
          <button
            type="button"
            onClick={() => go({ kind: "settings" })}
            className="min-w-0 flex-1 rounded-md px-2 py-1 text-left active:bg-muted/60"
          >
            <p className="truncate text-[13px] font-medium">{user.displayName}</p>
            <p className="truncate text-[11px] text-muted-foreground">
              {user.role ? ROLE_LABELS[user.role] : ""} · {user.email}
            </p>
          </button>
          <Button variant="outline" size="sm" onClick={() => void logout()}>
            <LogOut /> Sign out
          </Button>
        </div>
      </TabTray>
    </>
  );
}

const Tab = ({
  icon,
  label,
  count,
  tint,
  dot,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count?: number;
  tint?: string;
  /** A second signal beside the count: a dot in this colour at the icon's other corner. */
  dot?: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    className={cn(
      "relative flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 pb-1 pt-1.5 text-[10.5px] transition-colors",
      "[&_svg]:size-5 [&_svg]:shrink-0",
      active ? "font-semibold text-foreground" : "text-muted-foreground active:text-foreground",
    )}
  >
    {active ? <span aria-hidden className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-primary" /> : null}
    <span className="relative" style={active ? { color: "var(--primary)" } : undefined}>
      {icon}
      {dot ? (
        <span
          aria-hidden
          className="absolute -left-1.5 -top-1 size-2.5 rounded-full ring-2 ring-surface"
          style={{ backgroundColor: dot }}
        />
      ) : null}
      {count !== undefined && count > 0 ? (
        <span
          className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full px-1 text-center text-[9.5px] font-bold leading-4 tabular-nums text-background"
          style={{ backgroundColor: tint ?? "var(--muted-foreground)" }}
        >
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </span>
    {label}
  </button>
);

const TrayRow = ({
  icon,
  label,
  count,
  tint,
  active,
  note,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count?: number;
  /** A second, labelled figure — My tasks' cards to review. */
  note?: string;
  /** Colours the icon and makes the count a pill — for a row asking something of the user. */
  tint?: string;
  active?: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-[14px] transition-colors [&_svg]:size-4.5 [&_svg]:shrink-0",
      active ? "bg-accent/50 font-medium" : "active:bg-muted/60",
    )}
  >
    <span className="text-muted-foreground" style={tint ? { color: tint } : undefined}>
      {icon}
    </span>
    <span className="flex-1 text-left">{label}</span>
    {note ? <span className="text-[12px]" style={{ color: "var(--kind-review)" }}>{note}</span> : null}
    {count !== undefined && count > 0 ? (
      tint ? (
        <span
          className="rounded-full px-1.5 text-[11px] font-semibold tabular-nums"
          style={{ color: tint, backgroundColor: `color-mix(in oklab, ${tint} 16%, transparent)` }}
        >
          {count}
        </span>
      ) : (
        <span className="text-[12px] tabular-nums text-muted-foreground">{count}</span>
      )
    ) : null}
    <ChevronRight className="text-muted-foreground/50" />
  </button>
);

/** A panel that rises from the bottom edge, over the tab bar that opened it. */
const TabTray = ({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => (
  <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/45 motion-fade" />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className={cn(
          "fixed inset-x-0 bottom-[var(--kb,0px)] z-50 mx-auto flex max-h-[80dvh] w-full max-w-xl flex-col",
          "rounded-t-2xl border-t border-border bg-elevated pb-[env(safe-area-inset-bottom)] shadow-2xl",
          "motion-tray",
        )}
      >
        <div className="flex items-center px-4 pb-2 pt-3">
          <DialogPrimitive.Title className="flex-1 text-[15px] font-semibold tracking-tight">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Close className="rounded-sm p-1 text-muted-foreground hover:bg-muted" aria-label="Close">
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </Dialog>
);
